import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Star, MousePointerClick, Check, Loader2 } from "lucide-react";

const STAT_CARDS = [
  ["total_users", "Users"], ["total_properties", "Properties"], ["total_photos", "Photos documented"],
  ["comparisons_completed", "AI comparisons"], ["dispute_letters_generated", "Dispute letters"],
  ["pdfs_finalized", "PDFs finalized"], ["share_links_created", "Share links"],
  ["reports_viewed", "Reports viewed"], ["baselines_signed", "Baselines signed"],
  ["feedback_count", "Feedback received"], ["avg_feedback_rating", "Avg rating"],
  ["first_lock_nudges_shown", "First-lock nudges"], ["premium_subscribers", "Premium subs"],
];

const relTime = (d) => {
  try {
    const s = Math.max(1, Math.floor((Date.now() - new Date(d).getTime()) / 1000));
    if (s < 60) return `${s}s ago`;
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
  } catch { return ""; }
};

const CTA_SOURCE_LABEL = {
  hero: "Hero", nav: "Nav bar", pricing_free: "Pricing (Free)",
  cta_band: "Mid CTA band", final_band: "Final CTA band", unknown: "Unknown",
};

export default function AdminFeedback() {
  const navigate = useNavigate();
  const [state, setState] = useState("loading");
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState(""); // "" | new | reviewed
  const [skip, setSkip] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [expanded, setExpanded] = useState(new Set());
  const [summary, setSummary] = useState(null);
  const [cta, setCta] = useState(null);
  const PAGE = 50;

  const loadList = useCallback(async (nextSkip = 0, append = false) => {
    setListLoading(true);
    try {
      const params = { limit: PAGE, skip: nextSkip };
      if (statusFilter) params.status = statusFilter;
      const { data } = await api.get("/admin/feedback", { params });
      setItems((prev) => (append ? [...prev, ...data] : data));
      setHasMore(data.length === PAGE);
      setSkip(nextSkip);
    } catch (e) { console.error("loadList (admin feedback) failed", e); }
    setListLoading(false);
  }, [statusFilter]);

  const loadSummary = useCallback(async () => {
    try { const { data } = await api.get("/admin/feedback/summary"); setSummary(data); }
    catch (e) { console.error("feedback summary failed", e); }
  }, []);

  const markReviewed = async (id) => {
    const prev = items;
    setItems((arr) => arr.map((f) => (f.id === id ? { ...f, status: "reviewed" } : f)));
    try { await api.patch(`/admin/feedback/${id}`, { status: "reviewed" }); loadSummary(); }
    catch { setItems(prev); }
  };

  const toggleExpand = (id) => setExpanded((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  useEffect(() => {
    api.get("/admin/usage-stats")
      .then(({ data }) => { setStats(data); setState("ok"); })
      .catch(() => setState("denied"));
  }, []);

  useEffect(() => { if (state === "ok") { loadList(0, false); loadSummary(); } }, [state, loadList, loadSummary]);

  useEffect(() => {
    if (state !== "ok") return;
    api.get("/admin/landing-cta-stats").then(({ data }) => setCta(data)).catch(() => {});
  }, [state]);

  if (state === "denied") {
    return (
      <div className="min-h-screen grid place-items-center bg-[#EEF2F6] px-6 text-center" data-testid="admin-denied">
        <div>
          <h1 className="font-heading text-3xl font-bold text-navy">404</h1>
          <p className="mt-2 text-[#475569]">This page could not be found.</p>
          <Button onClick={() => navigate("/dashboard")} variant="outline" className="mt-4 rounded-xl">Go to dashboard</Button>
        </div>
      </div>
    );
  }

  return (
    <AppShell>
      <p className="text-sm font-semibold uppercase tracking-[0.1em] text-[#94A3B8]">Admin</p>
      <h1 className="mt-1 font-heading text-3xl font-bold tracking-tight text-navy">Usage & Feedback</h1>

      {/* Usage stats grid */}
      {state === "loading" || !stats ? (
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {STAT_CARDS.map((_, i) => <Skeleton key={i} className="h-24 rounded-2xl" />)}
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="usage-stats-grid">
          {STAT_CARDS.map(([key, label]) => (
            <div key={key} data-testid={`stat-${key}`} className="rounded-2xl border border-[#E2E8F0] bg-white p-4">
              <p className="font-heading text-3xl font-bold text-navy">
                {stats[key] === null || stats[key] === undefined ? "—" : stats[key]}
                {key === "avg_feedback_rating" && stats[key] != null && <span className="text-lg text-[#94A3B8]"> / 5</span>}
              </p>
              <p className="mt-1 text-xs uppercase tracking-wide text-[#94A3B8]">{label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Landing CTA clicks (7 days) */}
      {stats && (
        <div className="mt-6 rounded-2xl border border-[#E2E8F0] bg-white p-5" data-testid="landing-cta-tile">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <MousePointerClick size={18} className="text-brand" />
              <h2 className="font-heading font-semibold text-navy">Landing CTA clicks</h2>
              <span className="text-xs text-[#94A3B8]">last 7 days</span>
            </div>
            <span className="text-sm text-[#475569]" data-testid="cta-total">
              {cta ? `${cta.total} clicks · ${cta.demo_clicks} demo views` : "—"}
            </span>
          </div>
          {!cta ? (
            <Skeleton className="mt-4 h-16 rounded-xl" />
          ) : cta.total === 0 ? (
            <p className="mt-3 text-sm text-[#94A3B8]" data-testid="cta-empty">No CTA clicks recorded yet.</p>
          ) : (
            <div className="mt-4 space-y-2.5" data-testid="cta-by-source">
              {cta.by_source.map((r) => {
                const pct = Math.round((r.count / cta.total) * 100);
                return (
                  <div key={r.source} data-testid={`cta-source-${r.source}`} className="flex items-center gap-3">
                    <span className="w-32 shrink-0 text-sm text-[#475569]">{CTA_SOURCE_LABEL[r.source] || r.source}</span>
                    <div className="flex-1 h-2.5 rounded-full bg-[#F1F5F9] overflow-hidden">
                      <div className="h-full rounded-full bg-brand" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="w-20 shrink-0 text-right text-sm font-semibold text-navy">
                      {r.count} <span className="text-xs font-normal text-[#94A3B8]">({pct}%)</span>
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Feedback management */}
      <div className="mt-10 flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-heading text-xl font-bold text-navy">Feedback</h2>
        {summary && (
          <p className="text-sm text-[#475569]" data-testid="feedback-summary">
            <span className="font-semibold text-navy">{summary.total}</span> total ·{" "}
            <span className="font-semibold text-brand">{summary.new_count}</span> new ·{" "}
            avg <span className="font-semibold text-navy">{summary.avg_rating_30d ?? "—"}</span>/5 (30d)
          </p>
        )}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="feedback-status-filters">
        {[["", "All"], ["new", "New"], ["reviewed", "Reviewed"]].map(([val, label]) => (
          <button key={val || "all"} data-testid={`fb-filter-${val || "all"}`} onClick={() => setStatusFilter(val)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${statusFilter === val ? "bg-navy text-white border-navy" : "bg-white text-[#475569] border-[#E2E8F0] hover:bg-[#F1F5F9]"}`}>
            {label}
          </button>
        ))}
      </div>

      {listLoading && items.length === 0 ? (
        <div className="mt-4 space-y-3">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-24 rounded-2xl" />)}</div>
      ) : items.length === 0 ? (
        <p className="mt-6 text-[#94A3B8]" data-testid="feedback-empty">No feedback yet.</p>
      ) : (
        <div className="mt-4 space-y-3" data-testid="feedback-list">
          {items.map((f) => {
            const isOpen = expanded.has(f.id);
            const long = (f.comment || "").length > 140;
            const shown = f.comment && !isOpen && long ? f.comment.slice(0, 140) + "…" : f.comment;
            const reviewed = f.status === "reviewed";
            return (
              <div key={f.id} data-testid={`feedback-item-${f.id}`} className="rounded-2xl border border-[#E2E8F0] bg-white p-4">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-1" data-testid={`fb-rating-${f.id}`}>
                    {f.rating ? [1, 2, 3, 4, 5].map((n) => (
                      <Star key={n} size={15} className={n <= f.rating ? "fill-amber text-amber" : "text-[#CBD5E1]"} />
                    )) : <span className="text-xs text-[#94A3B8]">No rating</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    <span data-testid={`fb-status-${f.id}`} className={`rounded-full text-[11px] font-semibold px-2 py-0.5 ${reviewed ? "bg-[#DCFCE7] text-[#166534]" : "bg-[#DBEAFE] text-[#1E40AF]"}`}>
                      {reviewed ? "Reviewed" : "New"}
                    </span>
                  </div>
                </div>
                {f.comment && (
                  <p className="mt-2 text-sm text-[#334155] whitespace-pre-wrap break-words">
                    “{shown}”
                    {long && (
                      <button data-testid={`fb-expand-${f.id}`} onClick={() => toggleExpand(f.id)} className="ml-1 text-brand font-medium">
                        {isOpen ? "less" : "more"}
                      </button>
                    )}
                  </p>
                )}
                <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-[#94A3B8]">
                  {(f.page_path || f.context) && (
                    <span className="rounded-full bg-[#F1F5F9] text-[#475569] font-semibold px-2 py-0.5">{f.page_path || f.context}</span>
                  )}
                  <span>{relTime(f.created_at)}</span>
                  <span>·</span>
                  <span data-testid={`fb-user-${f.id}`}>
                    {f.email ? `${f.email}${f.tier ? ` (${f.tier})` : ""}` : "anonymous"}
                  </span>
                </div>
                {!reviewed && (
                  <div className="mt-3">
                    <Button data-testid={`fb-review-${f.id}`} size="sm" variant="outline" onClick={() => markReviewed(f.id)}
                      className="rounded-lg">
                      <Check size={14} className="mr-1.5" /> Mark reviewed
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
          {hasMore && (
            <Button data-testid="feedback-load-more" variant="outline" disabled={listLoading}
              onClick={() => loadList(skip + PAGE, true)} className="w-full rounded-xl">
              {listLoading ? <Loader2 size={16} className="mr-2 animate-spin" /> : null} Load more
            </Button>
          )}
        </div>
      )}
    </AppShell>
  );
}
